import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatUSD } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FileText, ExternalLink, RotateCcw, AlertTriangle, Save, Ruler, Mail, MailWarning, Trash2, XCircle, Receipt, Layers } from "lucide-react";
import { STATUS_LABEL, FACTURACION_LABEL, ORDER_STATUSES, FACTURACION_STATUSES, BRAND, TIPO_UBICACION_LABEL } from "@/lib/constants";
import StandSupplierOrdersSection from "@/components/admin/StandSupplierOrdersSection";

const inputCls = "bg-zinc-950 border-zinc-800 focus:border-[#FF5A00] focus-visible:ring-[#FF5A00]/30 rounded-sm";

const STATUS_BADGE = {
  enviado_para_revisar: "bg-amber-500/10 text-amber-300 border-amber-500/40",
  aprobado: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
  rechazado: "bg-red-500/10 text-red-300 border-red-500/40",
};

export default function AdminStandDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stand, setStand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [observation, setObservation] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [motivo, setMotivo] = useState("");
  const [newFacturacion, setNewFacturacion] = useState("");
  const [pendientes, setPendientes] = useState([]);
  const [medidas, setMedidas] = useState({ ancho: 0, profundidad: 0, altura: 0, superficie: 0, tipo_ubicacion: "vecino_3p", base_price: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/stands/${id}`);
      setStand(data);
      setNewStatus(ORDER_STATUSES.includes(data.status) ? data.status : "");
      setMotivo(data.rechazo_motivo || "");
      setNewFacturacion(data.facturacion_status || "falta_facturar");
      setMedidas({
        ancho: data.ancho, profundidad: data.profundidad, altura: data.altura,
        superficie: data.superficie, tipo_ubicacion: data.tipo_ubicacion || "vecino_3p",
        base_price: data.base_price || 0,
      });
      api.get(`/expositor/stand/${data.stand_number}/validate`)
        .then((r) => setPendientes(r.data.pendientes || []))
        .catch(() => {});
    } catch { navigate("/admin/dashboard"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !stand) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...</div>;

  const cfg = stand.configuration;
  const up = cfg.upgrades || {};
  const items = up.items || [];
  const total = up.total || 0;
  const hasOrder = ORDER_STATUSES.includes(stand.status);

  const action = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error");
    }
  };

  const saveStatus = () => {
    if (!newStatus) return toast.error("Elegí un estado");
    if (newStatus === "rechazado" && !motivo.trim()) return toast.error("El motivo del rechazo es obligatorio");
    action(
      () => api.post(`/admin/stands/${stand.id}/status`, { status: newStatus, motivo: newStatus === "rechazado" ? motivo.trim() : null }),
      "Estado del pedido actualizado"
    );
  };

  const saveFacturacion = () => action(
    () => api.post(`/admin/stands/${stand.id}/facturacion`, { status: newFacturacion }),
    "Estado de facturación actualizado"
  );

  const resendEmail = () => action(
    () => api.post(`/admin/stands/${stand.id}/resend-email`),
    "Notificación reenviada"
  );

  const deletePedido = () => {
    if (!window.confirm(`¿Eliminar el pedido del stand ${stand.stand_number}? El stand volverá a estado editable y sin pedido.`)) return;
    action(() => api.delete(`/admin/stands/${stand.id}/pedido`), "Pedido eliminado");
  };

  const saveMedidas = async () => {
    const ancho = Number(medidas.ancho);
    const prof = Number(medidas.profundidad);
    const superficie = Number(medidas.superficie) || Math.round(ancho * prof * 100) / 100;
    await action(
      () => api.put(`/admin/stands/${stand.id}`, {
        ancho, profundidad: prof, altura: Number(medidas.altura),
        superficie, tipo_ubicacion: medidas.tipo_ubicacion,
        base_price: Number(medidas.base_price) || 0,
      }),
      "Espacio actualizado"
    );
  };

  const sendReminder = () => action(
    () => api.post(`/admin/stands/${stand.id}/reminder`, {}),
    "Recordatorio enviado al expositor"
  );

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <header className="border-b border-zinc-900 sticky top-0 z-10 backdrop-blur-xl bg-zinc-950/80">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" data-testid="back-dashboard-link" className="text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></Link>
            <img src={BRAND.LOGO_AM} alt="AM Norte" className="h-8" />
            <div className="h-6 w-px bg-zinc-800" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Espacio {stand.stand_number} · {stand.stand_type}</div>
              <div className="text-sm font-semibold">{stand.company || "Sin asignar"} · <span className="font-mono text-zinc-400 text-xs">{stand.code}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="modificar-como-expositor-btn"
              size="sm"
              variant="outline"
              className="border-[#FF5A00]/50 text-[#FF5A00] hover:bg-[#FF5A00]/10 rounded-sm h-8 text-[10px] font-semibold tracking-wider uppercase"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/stands/${stand.id}/admin-edit-enter`);
                  sessionStorage.setItem("am_admin_edit_mode", JSON.stringify({
                    standId: stand.id,
                    standNumber: data.stand_number,
                    primaryStand: data.primary_stand,
                    adminEmail: "",
                    adminName: "",
                    entryAt: new Date().toISOString(),
                    company: data.company || stand.company || "",
                    status: data.status,
                    requiresReason: data.requires_reason,
                    warning: data.warning,
                    backTo: `/admin/stand/${stand.id}`,
                  }));
                  sessionStorage.removeItem("mis_espacios");
                  window.dispatchEvent(new Event("am-admin-edit-changed"));
                  toast.success(`Modo Administrador · ${data.stand_number}`);
                  navigate(data.target_url);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "No se pudo abrir el modo edición");
                }
              }}
              title="Ingresar como administrador al recorrido del expositor"
            >
              Modificar / Completar como expositor
            </Button>
            <Badge variant="outline" data-testid="header-status-badge" className={`rounded-sm border ${STATUS_BADGE[stand.status] || "border-zinc-700 text-zinc-400"}`}>
              {STATUS_LABEL[stand.status] || stand.status}
            </Badge>
            {stand.status === "aprobado" && stand.facturacion_status && (
              <Badge variant="outline" data-testid="header-facturacion-badge" className="rounded-sm border-sky-500/40 bg-sky-500/10 text-sky-300">
                {FACTURACION_LABEL[stand.facturacion_status]}
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {stand.combo_detail && (
            <div data-testid="combo-detail-card" className="border border-[#FF5A00]/40 bg-[#FF5A00]/5 rounded-sm p-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-[0.2em] text-[#FF5A00] flex items-center gap-2"><Layers className="w-3.5 h-3.5" /> Unión de espacios contiguos</div>
                <Button
                  data-testid="separate-combo-btn"
                  size="sm" variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 rounded-sm h-8 text-xs"
                  onClick={() => {
                    if (!window.confirm("¿Separar los espacios? Cada stand volverá a sus medidas y precio originales.")) return;
                    action(() => api.post(`/admin/stands/${stand.id}/separate`), "Espacios separados");
                  }}
                >
                  Separar espacios
                </Button>
              </div>
              <div className="text-sm text-zinc-200 space-y-1">
                <div><b>Stands asociados:</b> {stand.combo_members?.join(" + ")}</div>
                <div className="text-xs text-zinc-400">
                  {stand.combo_detail.members?.map((m) => `${m.number}: ${m.ancho} × ${m.profundidad} m (${m.superficie} m²)`).join(" · ")}
                </div>
                <div><b>Medida resultante:</b> {stand.combo_detail.medida_resultante} · {stand.combo_detail.superficie} m²</div>
                <div><b>Paredes eliminadas:</b> {stand.combo_detail.paredes_eliminadas_detalle}</div>
                <div><b>Tipología calculada:</b> {TIPO_UBICACION_LABEL[stand.combo_detail.tipologia] || stand.combo_detail.tipologia}</div>
                <div className="text-xs text-zinc-400"><b>Fórmula:</b> {stand.combo_detail.formula}</div>
                <div><b>Total calculado:</b> {stand.combo_detail.total != null ? `USD ${Math.round(stand.combo_detail.total).toLocaleString("es-AR")} + IVA` : <span className="text-amber-300">PENDIENTE DE REVISIÓN</span>}</div>
                {stand.combo_detail.requires_manual_quote && (
                  <div className="text-xs text-amber-300 border-t border-zinc-800 pt-2 mt-1 font-medium">
                    A COTIZAR — Grupo Península / 1 pared. Requiere cotización manual y confirmación desde este panel.
                  </div>
                )}
                {stand.combo_detail.needs_review && !stand.combo_detail.requires_manual_quote && (
                  <div className="text-xs text-amber-300 border-t border-zinc-800 pt-2 mt-1">
                    Requiere revisión: {stand.combo_detail.review_reasons?.join(" ")} Podés corregir medidas, tipología y precio desde la pestaña &quot;Medidas y paneles&quot;.
                  </div>
                )}
              </div>
            </div>
          )}
          {stand.plan_master_review_needed && (
            <div data-testid="plan-master-review-alert" className="border border-amber-500/50 bg-amber-500/10 rounded-sm p-4 flex items-start gap-3">
              <div className="text-amber-300 shrink-0 mt-0.5">⚠</div>
              <div className="text-sm text-amber-100">
                <div className="font-medium mb-1">Este pedido fue generado con datos anteriores del plano y requiere revisión.</div>
                <div className="text-xs text-amber-200/80">
                  La versión actual del plano oficial ETAPA_2 introdujo cambios de medida y tipología en la serie T (bloques y adyacencias oficiales). El snapshot histórico del pedido se conservó intacto. Revisá con el expositor si el pedido debe recalcularse con las medidas actualizadas y ajustá desde &quot;Medidas y paneles&quot; si corresponde.
                </div>
              </div>
            </div>
          )}
          {stand.email_pending && (
            <div data-testid="email-pending-alert" className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-amber-300">
                <MailWarning className="w-4 h-4" /> Correo pendiente de reenvío — la última notificación no pudo enviarse.
              </div>
              <Button data-testid="resend-email-btn" onClick={resendEmail} size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Reenviar notificación
              </Button>
            </div>
          )}

          {stand.requires_review && (
            <div data-testid="requires-review-alert" className="border border-[#FF5A00]/40 bg-[#FF5A00]/5 rounded-sm p-4 text-sm text-[#FF5A00]">
              Nuevo pedido de FULL EQUIPADO para el stand {stand.stand_number}. Requiere revisión.
            </div>
          )}

          {stand.status === "rechazado" && stand.rechazo_motivo && (
            <div data-testid="rechazo-motivo-card" className="border border-red-500/40 bg-red-500/5 rounded-sm p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-red-400 mb-1 flex items-center gap-2"><XCircle className="w-3.5 h-3.5" /> Motivo del rechazo</div>
              <div className="text-sm text-zinc-200">{stand.rechazo_motivo}</div>
            </div>
          )}

          {pendientes.length > 0 && hasOrder && (
            <div data-testid="pendientes-alert" className="border border-zinc-700 bg-zinc-900 rounded-sm p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Pedido recibido con información pendiente</div>
              <ul className="text-sm text-zinc-300 list-disc list-inside space-y-0.5">
                {pendientes.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </div>
          )}

          <Tabs defaultValue="config">
            <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm">
              <TabsTrigger value="config" data-testid="tab-config" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Configuración</TabsTrigger>
              <TabsTrigger value="medidas" data-testid="tab-medidas" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Medidas y paneles</TabsTrigger>
              <TabsTrigger value="upgrades" data-testid="tab-upgrades" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Upgrades</TabsTrigger>
              {stand.full_equipado && (
                <TabsTrigger value="contratado" data-testid="tab-contratado" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Detalle contratado</TabsTrigger>
              )}
              <TabsTrigger value="proveedores" data-testid="tab-stand-proveedores" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Pedidos a proveedores</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Historial</TabsTrigger>
            </TabsList>

            <TabsContent value="config" className="space-y-4 mt-4">
              <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-5 grid md:grid-cols-2 gap-4 text-sm">
                  <Row k="Responsable" v={cfg.datos_facturacion?.responsable} />
                  <Row k="Email" v={cfg.datos_facturacion?.email} />
                  <Row k="WhatsApp" v={cfg.datos_facturacion?.whatsapp} />
                  <Row k="Nombre comercial" v={cfg.datos_facturacion?.nombre_comercial} />
                  <Row k="Diseño" v={cfg.diseno?.opcion === "A" ? "Estándar" : "Personalizado"} />
                  <Row k="Color LED" v={cfg.terminaciones?.led_color} />
                  <Row k="Color alfombra" v={cfg.terminaciones?.alfombra_color} />
                  <Row k="Opción TV" v={cfg.tv?.opcion} />
                  <Row k="Fecha del pedido" v={stand.submitted_at ? stand.submitted_at.slice(0, 16).replace("T", " ") : "—"} />
                </CardContent>
              </Card>

              <FileLink title="Logo" link={cfg.logo?.link} extra={cfg.logo?.requires_assistance && "Requiere asistencia de diseño"} />

              <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold mb-3">Paneles de gráfica</div>
                  <div className="space-y-2">
                    {cfg.diseno?.paneles?.map((p) => (
                      <div key={p.name} className="border border-zinc-800 rounded-sm p-3 flex justify-between items-center text-sm">
                        <div>
                          <div className="font-medium">{p.name} <span className="text-zinc-500 font-mono text-xs ml-2">{p.size}</span></div>
                          {p.observaciones && <div className="text-xs text-zinc-500 mt-1">{p.observaciones}</div>}
                        </div>
                        {p.link ? <a href={p.link} target="_blank" rel="noopener noreferrer" className="text-[#FF5A00] text-xs flex items-center gap-1">Ver <ExternalLink className="w-3 h-3" /></a> : <span className="text-zinc-600 text-xs">Sin archivo</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {cfg.tv?.link && <FileLink title="Video TV" link={cfg.tv.link} />}

              <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold mb-3">Facturación</div>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <Row k="Razón social" v={cfg.datos_facturacion?.razon_social} />
                    <Row k="CUIT" v={cfg.datos_facturacion?.cuit} />
                    <Row k="Condición IVA" v={cfg.datos_facturacion?.condicion_iva} />
                    <Row k="Domicilio" v={cfg.datos_facturacion?.domicilio} />
                    <Row k="Email factura" v={cfg.datos_facturacion?.email_factura} />
                    <Row k="Observaciones" v={cfg.datos_facturacion?.observaciones} />
                  </div>
                </CardContent>
              </Card>

              {stand.observations?.length > 0 && (
                <Card className="bg-amber-500/5 border-amber-500/30 rounded-sm">
                  <CardContent className="p-5">
                    <div className="text-sm font-semibold mb-3 flex items-center text-amber-300"><AlertTriangle className="w-4 h-4 mr-2" /> Observaciones</div>
                    {stand.observations.map((o) => (
                      <div key={o.date} className="text-xs text-zinc-300 border-l-2 border-amber-500/50 pl-3 py-1 mb-1">
                        <span className="text-zinc-500 font-mono mr-2">{o.date?.slice(0,16).replace("T"," ")}</span>{o.comment}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="medidas" className="mt-4">
              {(stand.configuration?.diseno?.paneles_huerfanos || []).length > 0 && (
                <Card data-testid="paneles-huerfanos-alert" className="bg-amber-500/5 border-amber-500/40 rounded-sm mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-amber-100 space-y-2">
                        <div className="font-semibold text-amber-200">Se recalcularon los paneles gráficos.</div>
                        <div>Cambió la cantidad de paredes y la configuración gráfica fue actualizada. Existen {stand.configuration.diseno.paneles_huerfanos.length} archivo(s) asociado(s) a paneles eliminados.</div>
                        <ul className="text-[11px] font-mono text-amber-100/90 space-y-1 pt-1">
                          {stand.configuration.diseno.paneles_huerfanos.map((h, i) => (
                            <li key={`${h.link || h.name || "orphan"}-${i}`}>· {h.name} — <a className="underline decoration-dotted hover:text-white" href={h.link} target="_blank" rel="noreferrer">{h.link}</a>{h.observaciones ? ` — ${h.observaciones}` : ""}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-sm font-semibold mb-4"><Ruler className="w-4 h-4 text-[#FF5A00]" /> Medidas del espacio contratado</div>
                  <div className="grid sm:grid-cols-4 gap-3 mb-4">
                    <Fld label="Ancho (m)"><Input data-testid="medidas-ancho" type="number" step="0.01" className={inputCls} value={medidas.ancho} onChange={(e) => setMedidas({ ...medidas, ancho: e.target.value })} /></Fld>
                    <Fld label="Profundidad (m)"><Input data-testid="medidas-prof" type="number" step="0.01" className={inputCls} value={medidas.profundidad} onChange={(e) => setMedidas({ ...medidas, profundidad: e.target.value })} /></Fld>
                    <Fld label="Altura (m)"><Input data-testid="medidas-altura" type="number" step="0.01" className={inputCls} value={medidas.altura} onChange={(e) => setMedidas({ ...medidas, altura: e.target.value })} /></Fld>
                    <Fld label="Superficie (m²)"><Input data-testid="medidas-superficie" type="number" step="0.01" className={inputCls} value={medidas.superficie} onChange={(e) => setMedidas({ ...medidas, superficie: e.target.value })} /></Fld>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mb-4">
                    <Fld label="Tipo de ubicación">
                      <Select value={medidas.tipo_ubicacion} onValueChange={(v) => setMedidas({ ...medidas, tipo_ubicacion: v })}>
                        <SelectTrigger data-testid="medidas-tipo" className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                          <SelectItem value="esquina_2p">Esquina / 2 paredes</SelectItem>
                          <SelectItem value="vecino_3p">Entre vecinos / 3 paredes</SelectItem>
                          <SelectItem value="peninsula_1p">Península / 1 pared</SelectItem>
                          <SelectItem value="isla_0p">Isla / sin paredes</SelectItem>
                          <SelectItem value="sin_definir">A definir</SelectItem>
                          <SelectItem value="especial">Especial · requiere validación</SelectItem>
                        </SelectContent>
                      </Select>
                    </Fld>
                    <Fld label="Valor base Paquete (USD)"><Input data-testid="medidas-price" type="number" step="1" className={inputCls} value={medidas.base_price} onChange={(e) => setMedidas({ ...medidas, base_price: e.target.value })} /></Fld>
                  </div>
                  <div className="text-[11px] text-zinc-500 mb-3">
                    Al cambiar medida o tipo de ubicación, el valor base se recalcula automáticamente si coincide con la tabla comercial. Podés ajustarlo manualmente antes de guardar.
                  </div>
                  <Button data-testid="medidas-save-btn" onClick={saveMedidas} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
                    <Save className="w-4 h-4 mr-2" /> Guardar espacio
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 rounded-sm mt-4" data-testid="contratacion-card">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold mb-3">Tipo de contratación</div>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <Badge data-testid="contratacion-badge" className={`rounded-sm border ${stand.full_equipado ? "bg-[#FF5A00]/10 text-[#FF5A00] border-[#FF5A00]/40" : "bg-teal-500/10 text-teal-300 border-teal-500/40"}`}>
                        {stand.full_equipado ? "FULL EQUIPADO" : "SOLO ESPACIO"}
                      </Badge>
                      <div className="text-[11px] text-zinc-500 mt-2 max-w-md">
                        {stand.full_equipado
                          ? "El expositor accede al configurador del Paquete Full Equipado."
                          : "El expositor accede a Servicios Oficiales: Proyecto Especial o Alquiler de técnica y mobiliario."}
                      </div>
                    </div>
                    <Button
                      data-testid="toggle-full-equipado-btn"
                      variant="outline"
                      className="border-zinc-700 hover:bg-zinc-800 rounded-sm shrink-0 text-xs"
                      onClick={() => action(() => api.put(`/admin/stands/${stand.id}`, { full_equipado: !stand.full_equipado }), "Tipo de contratación actualizado")}
                    >
                      {stand.full_equipado ? "Marcar como SOLO ESPACIO" : "Marcar como FULL EQUIPADO"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 rounded-sm mt-4">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold mb-3">Paneles ({cfg.diseno?.paneles?.length || 0})</div>
                  <div className="space-y-2 text-sm">
                    {cfg.diseno?.paneles?.map((p) => (
                      <div key={p.name} className="flex justify-between border border-zinc-800 rounded-sm p-2">
                        <span>{p.name}</span>
                        <span className="font-mono text-zinc-400">{p.size}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {stand.full_equipado && <TiraLedManualControl stand={stand} onSaved={load} />}
            </TabsContent>

            <TabsContent value="upgrades" className="mt-4">
              <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold mb-3">Detalle de upgrades (USD)</div>
                  {items.length === 0 ? (
                    <div className="text-sm text-zinc-500">Sin upgrades seleccionados.</div>
                  ) : (
                    <>
                      <div className="border border-zinc-800 rounded-sm overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-zinc-950 text-zinc-500 text-[10px] uppercase">
                            <tr><th className="text-left p-3">Item</th><th className="text-right p-3">Cant.</th><th className="text-right p-3">P.U.</th><th className="text-right p-3">Subtotal</th></tr>
                          </thead>
                          <tbody>
                            {items.map((it) => (
                              <tr key={it.upgrade_id} className="border-t border-zinc-800">
                                <td className="p-3">{it.name}</td>
                                <td className="p-3 text-right font-mono">{it.quantity}</td>
                                <td className="p-3 text-right font-mono">{formatUSD(it.unit_price)}</td>
                                <td className="p-3 text-right font-mono">{formatUSD(it.unit_price * it.quantity)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 text-right">
                        <span className="text-xs text-zinc-500 uppercase tracking-wider mr-3">Total</span>
                        <span className="font-mono text-lg text-[#FF5A00]">{formatUSD(total)}</span>
                      </div>
                    </>
                  )}
                  {up.otros_request && (
                    <div className="mt-4 p-3 border border-dashed border-zinc-700 rounded-sm bg-zinc-950">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Pedido especial (Otros)</div>
                      <div className="text-sm text-zinc-200">{up.otros_request}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contratado" className="mt-4">
              <DetalleContratado standId={stand.id} onSaved={load} />
            </TabsContent>

            <TabsContent value="proveedores" className="mt-4">
              <StandSupplierOrdersSection stand={stand} onCreated={load} />
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-5 space-y-2">
                  {(stand.history || []).slice().reverse().map((h, i) => (
                    <div key={`${h.date}-${h.action}-${i}`} className="text-xs border-l-2 border-zinc-700 pl-3 py-1">
                      <span className="text-zinc-500 font-mono mr-2">{h.date?.slice(0,19).replace("T"," ")}</span>
                      <span className="text-[#FF5A00] mr-2">{h.action}</span>
                      <span className="text-zinc-400">{h.user}</span>
                      {h.comment && <div className="text-zinc-500 mt-0.5">{h.comment}</div>}
                    </div>
                  ))}
                  {(!stand.history || stand.history.length === 0) && <div className="text-xs text-zinc-500">Sin movimientos.</div>}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-5 space-y-3">
              <div className="text-sm font-semibold">Estado del pedido</div>
              <div className="text-xs text-zinc-500">
                Estado actual: <span className={`font-semibold ${stand.status === "aprobado" ? "text-emerald-300" : stand.status === "rechazado" ? "text-red-300" : stand.status === "enviado_para_revisar" ? "text-amber-300" : "text-zinc-400"}`}>{STATUS_LABEL[stand.status] || stand.status}</span>
              </div>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select" className={inputCls}>
                  <SelectValue placeholder={hasOrder ? "Elegir estado" : "Sin pedido enviado"} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                  {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
              {newStatus === "rechazado" && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-red-400 mb-1.5 block">Motivo del rechazo (obligatorio)</Label>
                  <Textarea data-testid="motivo-rechazo-input" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} className={inputCls} placeholder="Ej: la gráfica del panel 1 no cumple los requisitos técnicos..." />
                </div>
              )}
              <Button data-testid="status-save-btn" onClick={saveStatus} disabled={!newStatus} className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">Guardar estado</Button>
            </CardContent>
          </Card>

          {stand.status === "aprobado" && (
            <Card className="bg-zinc-900 border-sky-500/30 rounded-sm" data-testid="facturacion-card">
              <CardContent className="p-5 space-y-3">
                <div className="text-sm font-semibold flex items-center gap-2"><Receipt className="w-4 h-4 text-sky-400" /> Estado de facturación y pago</div>
                <div className="text-xs text-zinc-500">
                  Actual: <span className="text-sky-300 font-semibold">{FACTURACION_LABEL[stand.facturacion_status] || "FALTA FACTURAR"}</span>
                </div>
                <Select value={newFacturacion} onValueChange={setNewFacturacion}>
                  <SelectTrigger data-testid="facturacion-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                    {FACTURACION_STATUSES.map((s) => <SelectItem key={s} value={s}>{FACTURACION_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="facturacion-save-btn" onClick={saveFacturacion} className="w-full bg-sky-600 hover:bg-sky-700 rounded-sm">Guardar facturación</Button>
              </CardContent>
            </Card>
          )}

          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-5 space-y-3">
              <div className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-zinc-400" /> Notificaciones</div>
              <Button data-testid="resend-email-side-btn" onClick={resendEmail} disabled={!hasOrder} variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800 rounded-sm">
                Reenviar notificación
              </Button>
              <Button data-testid="reminder-btn" onClick={sendReminder} variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800 rounded-sm">
                Enviar recordatorio de datos faltantes
              </Button>
              {stand.last_email_at && (
                <div className="text-[11px] text-zinc-500">Último correo: {stand.last_email_type} · {stand.last_email_at.slice(0, 16).replace("T", " ")}</div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-5 space-y-3">
              <div className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Observación</div>
              <Textarea data-testid="observation-input" value={observation} onChange={(e) => setObservation(e.target.value)} rows={3} className={inputCls} placeholder="Ej: falta logo en formato curvas..." />
              <Button data-testid="observation-btn" onClick={() => observation && action(async () => { await api.post(`/admin/stands/${stand.id}/observation`, { comment: observation }); setObservation(""); }, "Observación registrada")} variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800 rounded-sm">Registrar observación</Button>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-5 space-y-3">
              <div className="text-sm font-semibold">Gestión del pedido</div>
              <Button data-testid="reopen-btn" onClick={() => action(() => api.post(`/admin/stands/${stand.id}/reopen`), "Configuración reabierta")} variant="outline" className="w-full border-zinc-700 hover:bg-zinc-800 rounded-sm">
                <RotateCcw className="w-3.5 h-3.5 mr-2" /> Reabrir configuración
              </Button>
              <Button data-testid="delete-pedido-btn" onClick={deletePedido} disabled={!hasOrder} variant="outline" className="w-full border-red-900 text-red-400 hover:bg-red-950 rounded-sm">
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Eliminar pedido
              </Button>
              <div className="text-[11px] text-zinc-500">Eliminar el pedido no borra el stand ni la configuración cargada: sólo revierte el pedido para que el expositor pueda editar y reenviar.</div>
            </CardContent>
          </Card>
        </aside>
      </main>
    </div>
  );
}

const Fld = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 border-b border-zinc-800 pb-1.5">
    <dt className="text-zinc-500 text-xs uppercase tracking-wider">{k}</dt>
    <dd className="text-zinc-100 text-right truncate max-w-[200px]">{v || "—"}</dd>
  </div>
);

const FileLink = ({ title, link, extra }) => (
  <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
    <CardContent className="p-5 flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-[#FF5A00]" /> {title}</div>
        {extra && <div className="text-xs text-amber-400 mt-1">{extra}</div>}
      </div>
      {link ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm text-[#FF5A00] hover:underline flex items-center gap-1">Abrir <ExternalLink className="w-3.5 h-3.5" /></a> : <span className="text-xs text-zinc-600">Sin archivo</span>}
    </CardContent>
  </Card>
);


function TiraLedManualControl({ stand, onSaved }) {
  const elem = ((stand.configuration?.paquete?.elementos) || []).find((e) => e.item_id === "tira_led");
  const isManual = !!(elem && elem.manual_override);
  const currentInc = elem ? Number(elem.included_qty || 0) : 0;
  const [meters, setMeters] = useState(String(currentInc || ""));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setMeters(String(currentInc || "")); }, [currentInc]);

  const setManual = async () => {
    const n = parseFloat(meters);
    if (isNaN(n) || n < 0) return toast.error("Ingresá una cantidad de metros válida");
    setSaving(true);
    try {
      await api.put(`/admin/stands/${stand.id}/tira-led-manual`, { meters: n });
      toast.success("Medida manual de tira LED aplicada");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const backToAuto = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/stands/${stand.id}/tira-led-manual`, { meters: null });
      toast.success("Tira LED vuelve a cálculo automático");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="tira-led-manual-card" className="bg-zinc-900 border-zinc-800 rounded-sm mt-4">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-sm font-semibold mb-3"><Ruler className="w-4 h-4 text-[#FF5A00]" /> Tira LED inferior</div>
        <div className="text-[11px] text-zinc-500 mb-3">
          Los metros por defecto se calculan automáticamente a partir del perímetro de los paneles vigentes.
          Podés forzar una medida manual cuando el proyecto lo requiera.
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className={`rounded-sm text-[10px] ${isManual ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"}`}>
            {isManual ? "Medida manual activa" : "Cálculo automático"}
          </Badge>
          <div className="text-xs text-zinc-400 font-mono">Vigente: {currentInc.toLocaleString()} m</div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Metros manuales</Label>
            <Input
              data-testid="tira-led-manual-input"
              className={inputCls}
              type="number"
              min="0"
              step="0.1"
              value={meters}
              onChange={(e) => setMeters(e.target.value)}
            />
          </div>
          <Button data-testid="tira-led-manual-save" onClick={setManual} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Forzar manual</>}
          </Button>
          {isManual && (
            <Button data-testid="tira-led-manual-reset" onClick={backToAuto} disabled={saving} variant="outline" className="border-zinc-700 hover:bg-zinc-800 rounded-sm">
              <RotateCcw className="w-4 h-4 mr-2" /> Volver a automático
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function DetalleContratado({ standId, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ name: "", cantidad: 1, unit: "u", valor_unit_venta: "", iva_rate: 21, a_cotizar: false, description: "", visible_to_client: true });
  const [creating, setCreating] = useState(false);
  const [adj, setAdj] = useState({ active: false, amount: 0, motivo: "" });
  const [adjBusy, setAdjBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/stands/${standId}/detalle-contratado`);
      setData(data);
      const a = data.pricing?.ajuste || {};
      setAdj({ active: !!a.active, amount: a.amount || 0, motivo: a.motivo || "" });
    } finally { setLoading(false); }
  }, [standId]);
  useEffect(() => { load(); }, [load]);

  const addItem = async () => {
    if (!newItem.name.trim()) return toast.error("El ítem manual necesita un nombre");
    setCreating(true);
    try {
      await api.post(`/admin/stands/${standId}/manual-items`, { ...newItem, cantidad: Number(newItem.cantidad || 1), valor_unit_venta: newItem.valor_unit_venta === "" ? null : Number(newItem.valor_unit_venta) });
      toast.success("Ítem manual agregado");
      setNewItem({ name: "", cantidad: 1, unit: "u", valor_unit_venta: "", iva_rate: 21, a_cotizar: false, description: "", visible_to_client: true });
      await load(); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setCreating(false); }
  };

  const deleteItem = async (iid) => {
    if (!window.confirm("¿Eliminar ítem manual?")) return;
    try {
      await api.delete(`/admin/stands/${standId}/manual-items/${iid}`);
      toast.success("Ítem eliminado");
      await load(); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const saveAdj = async () => {
    if (adj.active && !adj.motivo.trim()) return toast.error("El ajuste manual requiere motivo obligatorio");
    setAdjBusy(true);
    try {
      await api.put(`/admin/stands/${standId}/manual-adjustment`, { active: adj.active, amount: Number(adj.amount || 0), motivo: adj.motivo });
      toast.success("Ajuste actualizado");
      await load(); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setAdjBusy(false); }
  };

  if (loading || !data) return <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando detalle contratado…</div>;

  const p = data.pricing;

  return (
    <div className="space-y-6" data-testid="detalle-contratado">
      {/* Bloques técnicos */}
      <div className="grid md:grid-cols-2 gap-4">
        <TechCard title="Stand" rows={[["Código", data.stand.code], ["Medida", data.stand.medida], ["Superficie", `${data.stand.superficie || 0} m²`], ["Configuración", data.stand.configuracion]]} />
        <TechCard title="Panelería" rows={[["Paneles vigentes", data.paneleria.cantidad], ...(data.paneleria.paneles.map((pn) => [pn.name, pn.size])), ["Estado", data.paneleria.estado]]} />
        <TechCard title="TV" rows={[["Cantidad", data.tv.cantidad], ["Tamaño", data.tv.tamano], ["Contenido", data.tv.contenido || "—"], ["Estado", data.tv.estado]]} />
        <TechCard title="Tira LED" rows={[["Color", data.tira_led.color || "—"], ["Metros automáticos", `${data.tira_led.metros_auto} m`], ["Metros manuales", data.tira_led.metros_manual != null ? `${data.tira_led.metros_manual} m` : "—"], ["Metros vigentes", `${data.tira_led.metros_vigentes} m`], ["USD / m", data.tira_led.descuento_por_metro], ["Estado", data.tira_led.estado]]} />
        <TechCard title="Alfombra" rows={[["Superficie", `${data.alfombra.superficie || 0} m²`], ["Color", data.alfombra.color || "—"], ["Estado", data.alfombra.estado]]} />
        <TechCard title="Mobiliario" rows={[["Mesas altas", data.mobiliario.mesas], ["Sillas altas", data.mobiliario.sillas]]} />
        <TechCard title="Logo corpóreo" rows={[["Cantidad", data.logo_corporeo.cantidad], ["Estado", data.logo_corporeo.estado]]} />
        <TechCard title="Iluminación" rows={[["Reflectores", data.iluminacion.reflectores], ["Estado", data.iluminacion.estado]]} />
      </div>

      {/* Ítems manuales admin */}
      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Ítems manuales del administrador ({data.items_manuales.length})</div>
          </div>
          <div className="space-y-2 mb-4">
            {data.items_manuales.length === 0 && <div className="text-xs text-zinc-500">Sin ítems manuales por ahora.</div>}
            {data.items_manuales.map((it) => (
              <div key={it.id} data-testid={`manual-item-${it.id}`} className="flex items-center justify-between border border-zinc-800 rounded-sm p-3 bg-zinc-950">
                <div className="min-w-0">
                  <div className="text-sm">{it.name} <span className="text-zinc-500">× {it.cantidad}{it.unit}</span></div>
                  <div className="text-[11px] text-zinc-500">
                    {it.a_cotizar ? "A cotizar" : it.precio_pendiente ? <span className="text-amber-300">Precio pendiente</span> : `USD ${it.valor_unit_venta} c/u · IVA ${it.iva_rate ?? 21}%`}
                    {it.visible_to_client === false ? " · Oculto al cliente" : ""}
                    {it.costo_interno ? <span className="text-zinc-600"> · costo int. USD {it.costo_interno}</span> : null}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => deleteItem(it.id)} className="border-red-500/30 text-red-300 hover:bg-red-500/10 rounded-sm h-8"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
          {/* Formulario nuevo item */}
          <div className="border border-dashed border-zinc-800 rounded-sm p-4 bg-zinc-950 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Agregar ítem manual</div>
            <div className="grid md:grid-cols-6 gap-2">
              <Input data-testid="mi-name" placeholder="Nombre" className={`${inputCls} md:col-span-2`} value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
              <Input data-testid="mi-qty" type="number" placeholder="Cant." className={inputCls} value={newItem.cantidad} onChange={(e) => setNewItem({ ...newItem, cantidad: e.target.value })} />
              <Input data-testid="mi-unit" placeholder="u/m/m2" className={inputCls} value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} />
              <Input data-testid="mi-price" type="number" placeholder="USD c/u" className={inputCls} value={newItem.valor_unit_venta} disabled={newItem.a_cotizar} onChange={(e) => setNewItem({ ...newItem, valor_unit_venta: e.target.value })} />
              <Input data-testid="mi-iva" type="number" placeholder="IVA %" className={inputCls} value={newItem.iva_rate} onChange={(e) => setNewItem({ ...newItem, iva_rate: Number(e.target.value) })} />
            </div>
            <Textarea data-testid="mi-desc" placeholder="Descripción / observaciones" className={inputCls} value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} rows={2} />
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-4 text-xs">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={newItem.a_cotizar} onChange={(e) => setNewItem({ ...newItem, a_cotizar: e.target.checked, valor_unit_venta: e.target.checked ? "" : newItem.valor_unit_venta })} /> A cotizar</label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={newItem.visible_to_client !== false} onChange={(e) => setNewItem({ ...newItem, visible_to_client: e.target.checked })} /> Visible al cliente</label>
              </div>
              <Button data-testid="mi-add-btn" onClick={addItem} disabled={creating} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Agregar ítem"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculador */}
      <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="calculador-card">
        <CardContent className="p-5">
          <div className="text-sm font-semibold mb-3">Calculador de propuesta</div>
          <div className="grid md:grid-cols-2 gap-2 text-sm">
            <PriceLine k="Precio base Full Equipado" v={formatUSD(p.precio_base)} />
            <PriceLine k="Descuentos por elementos retirados" v={p.descuentos_total ? `− ${formatUSD(p.descuentos_total)}` : formatUSD(0)} negative={p.descuentos_total > 0} />
            <PriceLine k="Upgrades" v={formatUSD(p.upgrades_total)} />
            <PriceLine k="Ítems manuales" v={formatUSD(p.items_manuales_total || 0)} />
            <PriceLine k="Subtotal neto" v={formatUSD(p.subtotal_neto)} emphasize />
            <PriceLine k={`IVA ${p.iva_por_elemento ? "(por elemento)" : `(${p.iva_rate_default}%)`}`} v={formatUSD(p.iva_total)} />
            <PriceLine k="Total calculado por el sistema" v={formatUSD(p.total_calculado)} emphasize />
            {p.ajuste?.active && (
              <>
                <PriceLine k={`Ajuste manual (${p.ajuste.motivo})`} v={`${p.ajuste.amount >= 0 ? "+" : ""}${formatUSD(p.ajuste.amount)}`} negative={p.ajuste.amount < 0} />
                <PriceLine k="Total propuesto por administración" v={formatUSD(p.total_propuesto)} highlight />
              </>
            )}
          </div>

          {/* Toggle de ajuste manual */}
          <div className="mt-5 border-t border-zinc-800 pt-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Ajuste manual del total</div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" data-testid="adj-active" checked={adj.active} onChange={(e) => setAdj({ ...adj, active: e.target.checked })} />
                Aplicar ajuste
              </label>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 block">Monto (USD)</Label>
                <Input data-testid="adj-amount" type="number" className={`${inputCls} w-32`} value={adj.amount} disabled={!adj.active} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} />
              </div>
              <div className="flex-1 min-w-[220px]">
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 block">Motivo (obligatorio)</Label>
                <Input data-testid="adj-motivo" className={inputCls} value={adj.motivo} disabled={!adj.active} onChange={(e) => setAdj({ ...adj, motivo: e.target.value })} />
              </div>
              <Button data-testid="adj-save" onClick={saveAdj} disabled={adjBusy} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
                {adjBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Guardar</>}
              </Button>
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">Nunca sobrescribe el cálculo original. Queda registrado en el historial.</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const TechCard = ({ title, rows }) => (
  <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
    <CardContent className="p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{title}</div>
      <div className="space-y-1 text-xs">
        {rows.map(([k, v], i) => (
          <div key={`${k}-${i}`} className="flex items-baseline justify-between gap-3 py-0.5 border-b border-zinc-950 last:border-0">
            <span className="text-zinc-400">{k}</span>
            <span className="text-white font-mono text-right truncate">{v ?? "—"}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const PriceLine = ({ k, v, emphasize, highlight, negative }) => (
  <div className={`flex items-baseline justify-between px-3 py-2 rounded-sm ${highlight ? "bg-[#FF5A00]/10 border border-[#FF5A00]/30" : emphasize ? "bg-zinc-950" : ""}`}>
    <span className={`text-xs ${negative ? "text-emerald-300" : "text-zinc-400"}`}>{k}</span>
    <span className={`font-mono text-sm ${highlight ? "text-[#FF5A00] text-lg" : negative ? "text-emerald-300" : emphasize ? "text-white font-semibold" : "text-white"}`}>{v}</span>
  </div>
);

